import { useEffect, useEffectEvent, useRef } from "react";
import { usePendingEdits } from "./pending-edits";

// Subscribes to the PendingEdits `saveTick`. The SaveBar bumps the tick
// once per successful commit cycle; any consumer of this hook closes its
// open editor in response.
//
// Partial failures do NOT bump the tick (SaveBar.signalSaved is only called
// on full success), so failed rows stay open with their inline error.
//
// Only an editor left as it was since the save began closes: the tick comes
// once the save has reloaded the page, long after the SaveBar is gone, and an
// editor opened (or reopened) in between holds input that save never saw
// (spark#315).

export function useAutoCloseOnSave(open: string | boolean | null, close: () => void): void {
  const ctx = usePendingEdits();
  const saveTick = ctx?.saveTick ?? 0;
  const saving = ctx?.saving ?? false;
  // null: no save under way. Otherwise whether `open` has changed since it began.
  const touchedSinceSaveBegan = useRef<boolean | null>(null);
  const seenTick = useRef(saveTick);

  useEffect(() => {
    if (touchedSinceSaveBegan.current === false) touchedSinceSaveBegan.current = true;
  }, [open]);

  useEffect(() => {
    if (saving) touchedSinceSaveBegan.current = false;
  }, [saving]);

  const closeUnlessTouched = useEffectEvent(() => {
    const touched = touchedSinceSaveBegan.current;
    touchedSinceSaveBegan.current = null;
    if (!touched) close();
  });

  useEffect(() => {
    if (seenTick.current === saveTick) return;
    seenTick.current = saveTick;
    closeUnlessTouched();
  }, [saveTick]);
}
